import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * AuthContext - Handles authentication, session management, and user profiles
 * Uses Supabase Auth with email/password
 */
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const loggingOut = useRef(false);

  // Check if Supabase is configured
  const isSupabaseConfigured = !!(
    process.env.REACT_APP_SUPABASE_URL &&
    process.env.REACT_APP_SUPABASE_ANON_KEY
  );

  // ─── FETCH USER PROFILE ───
  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setProfile(data);
      return data;
    } catch (err) {
      console.error('Error fetching profile:', err.message);
      return null;
    }
  }, []);

  // ─── INITIALISE SESSION ───
  useEffect(() => {
    if (!isSupabaseConfigured) {
      // Demo mode — no Supabase, fall back to sample users
      setLoading(false);
      return;
    }

    // Safety timeout — if auth init takes > 3s, stop loading and show login
    // Supabase lock can stall for 5s; don't make user wait that long
    const safetyTimeout = setTimeout(() => {
      console.warn('Auth initialization timed out after 3s — showing login');
      setLoading(false);
    }, 3000);

    let resolved = false;
    const finishLoading = () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(safetyTimeout);
        setLoading(false);
      }
    };

    // Listen for auth changes FIRST (catches session faster than getSession)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        // If we're in the middle of logging out, ignore any session restore events
        if (loggingOut.current) {
          if (event === 'SIGNED_OUT') {
            loggingOut.current = false; // signOut completed
          }
          finishLoading();
          return;
        }
        setSession(currentSession);
        if (currentSession?.user) {
          // Use setTimeout(0) so Supabase SDK fully internalises the session
          // before we query the profiles table (avoids RLS timing issues)
          setTimeout(async () => {
            await fetchProfile(currentSession.user.id);
            finishLoading();
          }, 0);
        } else {
          setProfile(null);
          finishLoading();
        }
      }
    );

    // Also try getSession as a fallback
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      if (currentSession?.user) {
        fetchProfile(currentSession.user.id)
          .then(() => finishLoading())
          .catch(() => finishLoading());
      } else {
        finishLoading();
      }
    }).catch((err) => {
      console.error('Error getting session:', err.message);
      finishLoading();
    });

    return () => {
      clearTimeout(safetyTimeout);
      subscription?.unsubscribe();
    };
  }, [isSupabaseConfigured, fetchProfile]);

  // ─── LOGIN ───
  const login = useCallback(async (email, password) => {
    setAuthError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      // onAuthStateChange will fire with SIGNED_IN event and handle
      // setting session + fetching profile. We just return the result here.
      // As a safety net, also set session eagerly so AuthGate can react.
      if (data.session) {
        setSession(data.session);
      }

      return { success: true, user: data.user };
    } catch (err) {
      setAuthError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  // ─── LOGOUT ───
  const logout = useCallback(async () => {
    // Set flag BEFORE clearing state — prevents onAuthStateChange from restoring session
    loggingOut.current = true;

    // Clear React state immediately so UI shows login
    setSession(null);
    setProfile(null);
    setAuthError(null);

    // Force-clear the stored Supabase session from localStorage
    try {
      const storageKey = `sb-ttrxwmqahwrenyhxzdbb-auth-token`;
      localStorage.removeItem(storageKey);
    } catch (_) { /* ignore */ }

    // Then tell Supabase to sign out (with timeout so it can't hang)
    try {
      const signOutPromise = supabase.auth.signOut({ scope: 'local' });
      const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 3000));
      await Promise.race([signOutPromise, timeoutPromise]);
    } catch (err) {
      console.error('Logout error:', err.message);
    }

    // Reset flag after a short delay (in case SIGNED_OUT event was missed)
    setTimeout(() => { loggingOut.current = false; }, 500);
  }, []);

  // ─── CHANGE PASSWORD ───
  const changePassword = useCallback(async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ─── ADMIN: CREATE USER ───
  // Creates auth user via signUp, then ensures a profile row exists
  const createUser = useCallback(async (email, name, role, password) => {
    try {
      // Create user via Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, role },
          emailRedirectTo: window.location.origin,
        },
      });
      if (error) throw error;

      // Ensure profile row exists (upsert to handle both trigger-created and missing cases)
      if (data.user) {
        const initials = name
          .split(' ')
          .map((w) => w[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);

        await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            name,
            initials,
            role,
            is_active: true,
          }, { onConflict: 'id' });
      }

      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ─── ADMIN: UPDATE USER ROLE ───
  const updateUserRole = useCallback(async (userId, newRole) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('id', userId);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ─── ADMIN: DEACTIVATE USER ───
  const deactivateUser = useCallback(async (userId) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', userId);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ─── ADMIN: FETCH ALL PROFILES ───
  const fetchAllProfiles = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching profiles:', err.message);
      return [];
    }
  }, []);

  // ─── CONTEXT VALUE ───
  const value = {
    // State
    session,
    profile,
    loading,
    authError,
    isAuthenticated: !!session && !!profile,
    isSupabaseConfigured,

    // Derived
    userId: session?.user?.id || null,
    userRole: profile?.role || null,
    userName: profile?.name || null,

    // Actions
    login,
    logout,
    changePassword,
    fetchProfile,

    // Admin
    createUser,
    updateUserRole,
    deactivateUser,
    fetchAllProfiles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
